import type { FlattenedPortfolioItem } from "@/types";

export enum RegistryEventType {
  ISSUANCE = "ISSUANCE",
  INCOMING_TRANSFER = "INCOMING_TRANSFER",
  OUTGOING_TRANSFER = "OUTGOING_TRANSFER",
  CANCEL = "CANCEL",
  EXPIRY = "EXPIRY",
  REVOCATION = "REVOCATION",
}

export type SortOrder = "asc" | "desc";

/**
 * @property company_id - The Soldera company holding the counterparty account, when it is one of our connections
 */
export interface RegistryEventCounterparty {
  name: string | null;
  account_number: string | null;
  org_eecs: string | null;
  company_id: number | null;
}

/**
 * The company's own registry connection the event was read from.
 *
 * @property region - Registry code such as `EE` or `BE-BRU`
 */
export interface RegistryEventConnection {
  registry_connection_id: number;
  integration_name: string;
  region: string;
  registry_eecs: string | null;
}

export type RegistryEventAllocationKind = "ALLOCATION" | "RETURN";

/**
 * The portfolio move behind a transfer between the company and Soldera: an allocation sends GOs to
 * Soldera's spot or escrow account, a return brings them back.
 *
 * @property portfolio_names - The portfolios the moved GOs landed in
 */
export interface RegistryEventAllocation {
  kind: RegistryEventAllocationKind;
  portfolio_names: string[];
}

/**
 * One registry transaction log event, reduced to the bundles linked to platform certificates.
 *
 * @property registry_event_id - The registry's own identifier, shown as the "Registry transaction" reference
 * @property linked_volume - MWh of the bundles linked to a platform certificate; the table shows only this
 * @property registry_volume - MWh of the whole event as the registry reported it
 * @property counterparty - Null on events without a counterparty (issuance, expiry, revocation)
 * @property allocation - Null unless a portfolio allocation or return drove the transfer
 * @property production_months - Distinct `YYYY-MM` production periods across the linked bundles
 */
export interface RegistryEventSummary {
  id: number;
  registry_event_id: string;
  company_id: number;
  company_name: string | null;
  event_type: RegistryEventType;
  occurred_at: string;
  linked_volume: number;
  linked_certificate_count: number;
  registry_volume: number;
  registry_certificate_count: number;
  counterparty: RegistryEventCounterparty | null;
  connection: RegistryEventConnection;
  allocation: RegistryEventAllocation | null;
  production_months: string[];
  technologies: string[];
  production_devices: string[];
}

/**
 * An event's type as the Transactions table and its Type filter show it: a transfer that an
 * allocation or return drove takes that kind instead of its registry type.
 */
export type EventRowType = RegistryEventType | RegistryEventAllocationKind;

export const EVENT_ROW_TYPES: EventRowType[] = [
  ...Object.values(RegistryEventType),
  "ALLOCATION",
  "RETURN",
];

export const eventRowType = (event: RegistryEventSummary): EventRowType =>
  event.allocation?.kind ?? event.event_type;

/**
 * @property portfolio_items - The company's portfolio item records on the certificates the event acted
 *   on, held or not; each item's `selected_count` is the event's share of it
 */
export interface RegistryEventDetail extends RegistryEventSummary {
  portfolio_items: FlattenedPortfolioItem[];
}

/**
 * @property next_cursor - Null on the last page
 * @property has_linked_events - Whether the companies have any event with a linked bundle, ignoring every filter
 */
export interface RegistryEventPage {
  events: RegistryEventSummary[];
  next_cursor: string | null;
  cut_inside_dense_stretch: boolean;
  has_linked_events: boolean;
}

/**
 * @property date_from - Inclusive ISO 8601 instant on `occurred_at`
 * @property date_before - Exclusive ISO 8601 instant on `occurred_at`
 * @property event_types - Empty means every type
 */
export interface RegistryEventsQuery {
  company_ids: number[];
  date_from: string | null;
  date_before: string | null;
  event_types: RegistryEventType[];
  sort_order: SortOrder;
}
